import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import ForceGraph from '@/components/blockchain/ForceGraph';
import GraphFilters from '@/components/blockchain/GraphFilters';
import GraphToolbar from '@/components/blockchain/GraphToolbar';
import NodeDetails from '@/components/blockchain/NodeDetails';
import GraphLegend from '@/components/blockchain/GraphLegend';
import SelectionPanel from '@/components/blockchain/SelectionPanel';
import EdgeCreatorModal from '@/components/blockchain/EdgeCreatorModal';
import useGraphStore from '@/store/graphStore';
import { useLanguage } from '@/context/LanguageContext';

const BlockchainFlow = () => {
  const { language } = useLanguage();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  const {
    initializeData,
    loading,
    error,
    sidebarOpen,
    toggleSidebar,
    filteredNodes,
    filteredEdges,
    selectedNodes,
    selectedNode,
    neighborhoodMode,
    connectMode,
    magnetMode,
  } = useGraphStore();
  
  const t = {
    title: language === 'de' ? 'Blockchain Fluss-Graph' : 'Blockchain Flow Graph',
    subtitle: language === 'de' 
      ? 'Visualisiere Kryptowährungs-Transaktionen und Geldflüsse' 
      : 'Visualize cryptocurrency transactions and fund flows',
    loading: language === 'de' ? 'Lade Daten...' : 'Loading data...',
    error: language === 'de' ? 'Fehler beim Laden' : 'Error loading data',
    noResults: language === 'de' ? 'Keine Ergebnisse' : 'No results',
    adjustFilters: language === 'de' ? 'Filter anpassen' : 'Adjust filters',
    filters: language === 'de' ? 'Filter' : 'Filters',
    neighborhoodView: language === 'de' ? 'Nachbarschaftsansicht' : 'Neighborhood View',
    connectModeActive: language === 'de' ? 'Verbindungsmodus aktiv' : 'Connect Mode Active',
    magnetMode: language === 'de' ? 'Magnet-Modus' : 'Magnet Mode',
    freeMove: language === 'de' ? 'Freie Bewegung' : 'Free Movement',
  };
  
  useEffect(() => {
    initializeData();
  }, [initializeData]);
  
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [sidebarOpen]);
  
  const handleExport = (format) => {
    const svgElement = containerRef.current?.querySelector('svg');
    if (!svgElement) return;
    
    if (format === 'svg') {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      downloadBlob(blob, 'blockchain-flow.svg');
    } else if (format === 'png') {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      
      img.onload = () => {
        canvas.width = dimensions.width * 2;
        canvas.height = dimensions.height * 2;
        ctx.scale(2, 2);
        ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--background').trim() || '#0f172a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => downloadBlob(blob, 'blockchain-flow.png'));
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } else if (format === 'json') {
      const data = { nodes: filteredNodes, edges: filteredEdges };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      downloadBlob(blob, 'blockchain-flow.json');
    } else if (format === 'csv') {
      const headers = ['id', 'address', 'label', 'balance', 'chain', 'risk', 'degree', 'txCount'];
      const rows = filteredNodes.map(n => headers.map(h => n[h]).join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      downloadBlob(blob, 'blockchain-nodes.csv');
    }
  };
  
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasSelection = selectedNodes.length > 0 || selectedNode;

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="blockchain-flow-page">
      {/* Header */}
      <div className="p-4 border-b border-border bg-card shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              <i className="fa-solid fa-diagram-project text-primary"></i>
              {t.title}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{t.subtitle}</p>
          </div>
          
          {/* Status badges */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <AnimatePresence>
              {!magnetMode && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <Badge variant="secondary" className="bg-green-500/10 text-green-500 text-xs">
                    <i className="fa-solid fa-arrows-up-down-left-right mr-1"></i>
                    {t.freeMove}
                  </Badge>
                </motion.div>
              )}
              {magnetMode && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <Badge variant="secondary" className="bg-purple-500/10 text-purple-500 text-xs">
                    <i className="fa-solid fa-magnet mr-1"></i>
                    {t.magnetMode}
                  </Badge>
                </motion.div>
              )}
              {neighborhoodMode && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <Badge variant="secondary" className="bg-primary/10 text-primary text-xs">
                    <i className="fa-solid fa-bullseye mr-1"></i>
                    {t.neighborhoodView}
                  </Badge>
                </motion.div>
              )}
              {connectMode && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <Badge variant="secondary" className="bg-warning/10 text-warning animate-pulse">
                    <i className="fa-solid fa-pen mr-1"></i>
                    {t.connectModeActive}
                  </Badge>
                </motion.div>
              )}
            </AnimatePresence>
            
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden">
                  <i className="fa-solid fa-filter mr-2"></i>
                  {t.filters}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0">
                <GraphFilters onClose={() => setMobileFiltersOpen(false)} />
              </SheetContent>
            </Sheet>
            
            <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={toggleSidebar}>
              <i className={cn("fa-solid", sidebarOpen ? "fa-angles-left" : "fa-angles-right")}></i>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Sidebar */}
        <motion.div
          initial={false}
          animate={{ width: sidebarOpen ? 288 : 0 }}
          className="hidden lg:flex flex-col border-r border-border overflow-hidden"
        >
          <GraphFilters className="flex-1" />
        </motion.div>
        
        {/* Graph Container */}
        <div className="flex-1 relative flex flex-col">
          {/* Toolbar */}
          <div className="absolute top-4 right-4 z-10">
            <GraphToolbar onExport={handleExport} />
          </div>
          
          {/* Legend */}
          <div className="absolute bottom-4 left-4 z-10 hidden sm:block">
            <GraphLegend />
          </div>
          
          {/* Selection Panel - shows when nodes selected */}
          <AnimatePresence>
            {hasSelection && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute top-20 right-4 z-10 w-72 hidden md:block"
              >
                <SelectionPanel />
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Node Details - shows when no multi-select */}
          <AnimatePresence>
            {!hasSelection && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute bottom-4 right-4 z-10 hidden md:block w-72"
              >
                <NodeDetails />
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Graph */}
          <div ref={containerRef} className="flex-1 relative">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  <motion.i
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    className="fa-solid fa-spinner text-3xl text-primary mb-3"
                  />
                  <p className="text-sm text-muted-foreground">{t.loading}</p>
                </motion.div>
              </div>
            ) : error ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <i className="fa-solid fa-triangle-exclamation text-3xl text-destructive mb-3"></i>
                  <p className="text-sm text-destructive">{t.error}</p>
                  <p className="text-xs text-muted-foreground mt-1">{error}</p>
                </div>
              </div>
            ) : filteredNodes.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <i className="fa-solid fa-filter-circle-xmark text-3xl text-muted-foreground mb-3"></i>
                  <p className="text-sm text-muted-foreground">{t.noResults}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.adjustFilters}</p>
                </div>
              </div>
            ) : (
              <ForceGraph width={dimensions.width} height={dimensions.height} />
            )}
          </div>
          
          {/* Mobile panels */}
          <div className="md:hidden border-t border-border bg-card p-4 space-y-4">
            {hasSelection ? <SelectionPanel /> : <NodeDetails />}
          </div>
        </div>
      </div>
      
      {/* Edge Creator Modal */}
      <EdgeCreatorModal />
    </div>
  );
};

export default BlockchainFlow;
